import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Toolbar
} from '@mui/material';
import {
  Edit,
  Delete,
  Search as SearchIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

const TermsTable = ({ 
  terms = [], 
  languages = [],
  onEditTerm, 
  onDeleteTerm,
  loading = false 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState('all');

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Filter and search terms
  const filteredTerms = useMemo(() => {
    return terms.filter(term => {
      // Search filter
      const matchesSearch = searchQuery.trim() === '' || 
        term.source_text?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Language filter
      const matchesLanguage = selectedLanguageFilter === 'all' || 
        term.translations?.some(t => 
          (t.language?.code === selectedLanguageFilter || t.language === selectedLanguageFilter) &&
          t.translated_text
        );
      
      return matchesSearch && matchesLanguage;
    });
  }, [terms, searchQuery, selectedLanguageFilter]);

  // Empty state
  if (terms.length === 0 && !loading) {
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          py: 8, 
          textAlign: 'center',
          bgcolor: 'grey.50'
        }}
      >
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No terms in this glossary yet
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Add your first term to get started
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filters Toolbar */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1 }}>
          <TextField
            size="small"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' }, flexGrow: 1 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 0, width: { xs: '100%', sm: 180 } }}>
            <InputLabel>Language Filter</InputLabel>
            <Select
              value={selectedLanguageFilter}
              onChange={(e) => setSelectedLanguageFilter(e.target.value)}
              label="Language Filter"
            >
              <MenuItem value="all">All Languages</MenuItem>
              {languages.map(lang => (
                <MenuItem key={lang.id} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredTerms.length} of {terms.length} terms
          </Typography>
        </Toolbar>
      </Paper>

      {/* Table */}
      <TableContainer 
        component={Paper} 
        variant="outlined"
        sx={{ 
          maxHeight: '600px',
          '& .MuiTableHead-root': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper'
          }
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>English Term</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>Translations</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>Updated By</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>Last Updated</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTerms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No terms match your search criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTerms.map((term) => {
                // Get list of languages with translations
                const translatedLanguages = term.translations 
                  ? term.translations
                      .filter(t => t.translated_text)
                      .map(t => t.language?.code || t.language?.name)
                      .filter(Boolean)
                  : [];
                
                return (
                  <TableRow 
                    key={term.id} 
                    hover
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'action.hover' 
                      },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontWeight: 500
                        }}
                      >
                        {term.source_text || ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {translatedLanguages.length > 0 ? (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {translatedLanguages.map((lang, idx) => (
                            <Chip 
                              key={idx}
                              label={lang} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          No translations
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2">
                        {term.created_by_username || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2">
                        {term.updated_by_username || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(term.updated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {onEditTerm ? (
                          <Tooltip title="Edit term">
                            <IconButton 
                              size="small" 
                              onClick={() => onEditTerm(term)}
                              sx={{ 
                                '&:hover': { 
                                  bgcolor: 'primary.lighter',
                                  color: 'primary.main' 
                                }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="View only - no edit permission">
                            <IconButton 
                              size="small" 
                              disabled
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDeleteTerm && (
                          <Tooltip title="Delete term">
                            <IconButton 
                              size="small" 
                              onClick={() => onDeleteTerm(term)}
                              sx={{ 
                                '&:hover': { 
                                  bgcolor: 'error.lighter',
                                  color: 'error.main' 
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default TermsTable;
